import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import PackageIcons from '@/icons/PackageIcons'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { abbreviateProductName } from '@/utils/function'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Typogaphy } from 'vnetwork-ui'
import {
    getStatsByProductGroup,
    StatsByProductGroupPayload,
} from '../../../services'
import { HalfChartSkeleton } from './skeletons'

interface StatsByProductGroupChartProps {
    filterPayload: StatsByProductGroupPayload
}

const StatsByProductGroupChart: React.FC<StatsByProductGroupChartProps> = ({
    filterPayload,
}) => {
    const { t } = useTranslation()

    // Fetch all product groups
    const { data: productGroupsData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS_LIST],
        queryFn: () => getProductsGroupOptions(),
    })
    const productGroups = productGroupsData || []

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MARKETING,
                'stats-by-product-group',
                filterPayload,
            ],
            queryFn: () => getStatsByProductGroup(filterPayload),
        },
        { loadingComponent: <HalfChartSkeleton /> }
    )

    const stats = data?.results || []

    const chartData = useMemo(() => {
        if (productGroups.length === 0) return []

        // Show all product groups, fill 0 for missing
        return productGroups.map((pg) => {
            const found = stats.find((item) => item?.product_group === pg.value)
            return {
                name: abbreviateProductName(pg.label, 30)?.toUpperCase(),
                unclassified: found?.unclassified ?? 0,
                warm: found?.warm_lead ?? 0,
                sql: found?.sale_qualified_lead ?? 0,
                cool: found?.cool_lead ?? 0,
                unqualified: found?.un_qualified_lead ?? 0,
            }
        })
    }, [stats, productGroups])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="bg-white rounded-md border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
                <PackageIcons className="text-primary w-4 h-4" />
                <Typogaphy className="font-medium text-primary capitalize">
                    {t('stats-by-product-group')}
                </Typogaphy>
            </div>

            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 9 }}
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={80}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                        contentStyle={{
                            fontSize: 12,
                            borderRadius: 8,
                            border: '1px solid #e5e7eb',
                        }}
                    />
                    <Legend
                        wrapperStyle={{ fontSize: 11 }}
                        iconType="square"
                        iconSize={10}
                    />
                    <Bar
                        dataKey="unclassified"
                        name={t('unclassified')}
                        stackId="a"
                        fill="#9ca3af"
                    />
                    <Bar
                        dataKey="warm"
                        name={t('warm-lead')}
                        stackId="a"
                        fill="#f59e0b"
                    />
                    <Bar dataKey="sql" name="SQL" stackId="a" fill="#22c55e" />
                    <Bar
                        dataKey="cool"
                        name={t('cool-lead')}
                        stackId="a"
                        fill="#3b82f6"
                    />
                    <Bar
                        dataKey="unqualified"
                        name={t('unqualified')}
                        stackId="a"
                        fill="#ef4444"
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}

export default StatsByProductGroupChart
